import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, ChevronDown, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  getActiveGroupVotesForOrganizer,
  deleteGroupVote,
} from '@/services/groupVoteService';
import { groupVoteKeys } from '@/lib/queryKeys';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { GroupVote } from '@/services/groupVoteService';
import { formatTimeDisplay } from '@/utils/formatTime';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/** Refetch every 60s when there are active votes (handles expiry). */
const REFETCH_INTERVAL_MS = 60_000;

/** Normalize slot time to HH:MM for URL params (matches timeslot format). */
function normalizeSlotTimeForUrl(time: string): string {
  if (typeof time !== 'string') return '00:00';
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : time;
}

const GroupVoteStickyIndicator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [voteToDelete, setVoteToDelete] = useState<GroupVote | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: activeVotes = [], isLoading } = useQuery({
    queryKey: groupVoteKeys.active(user?.id),
    queryFn: () => getActiveGroupVotesForOrganizer(user!.id),
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.data && query.state.data.length > 0 ? REFETCH_INTERVAL_MS : false,
  });

  if (!user || isLoading || activeVotes.length === 0) {
    return null;
  }

  const handleConfirmDelete = async () => {
    if (!voteToDelete || !user) return;
    setIsDeleting(true);
    const { success, error: deleteError } = await deleteGroupVote(voteToDelete.id);
    setIsDeleting(false);
    setVoteToDelete(null);
    if (success) {
      queryClient.invalidateQueries({ queryKey: groupVoteKeys.active(user.id) });
      toast.success('Vote supprimé');
    } else {
      toast.error(deleteError || 'Impossible de supprimer le vote');
    }
  };

  const handleVoteSelect = (vote: GroupVote) => {
    setOpen(false);
    const votesNeeded = Math.max(1, vote.maxParticipants - 1);
    const readySlot = vote.activityId
      ? vote.slots.find((s) => s.votesCount >= votesNeeded && s.isAvailable !== false)
      : undefined;
    if (readySlot && vote.activityId) {
      const slotTime = normalizeSlotTimeForUrl(readySlot.slotTime);
      navigate(
        `/booking/${vote.activityId}?groupVoteId=${vote.id}&slotDate=${readySlot.slotDate}&slotTime=${slotTime}&participants=${vote.maxParticipants}&numberOfGames=${vote.numberOfGames}`
      );
    } else {
      navigate('/account?tab=votes');
    }
  };

  const firstVote = activeVotes[0];

  const handleFirstClick = () => {
    if (activeVotes.length === 1) {
      handleVoteSelect(firstVote);
    } else {
      setOpen(true);
    }
  };

  const buttonContent = (
    <span className="flex items-center gap-2">
      <Users className="size-4 lg:size-[18px]" />
      <span className="font-medium text-xs lg:text-sm">
        {activeVotes.length > 1
          ? `${activeVotes.length} votes en cours`
          : 'Vote en cours'}
      </span>
      {activeVotes.length > 1 && (
        <ChevronDown className="size-3.5 lg:size-4 opacity-80" />
      )}
    </span>
  );

  if (activeVotes.length === 1) {
    return (
      <Button
        type="button"
        onClick={handleFirstClick}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 flex items-center gap-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-full shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 mb-[max(0,env(safe-area-inset-bottom))]"
        aria-label="Vote en cours"
      >
        {buttonContent}
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 flex items-center gap-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-full shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 mb-[max(0,env(safe-area-inset-bottom))]"
          aria-label="Votes en cours"
        >
          {buttonContent}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        sideOffset={8}
        className="max-w-[280px] overflow-hidden"
      >
        {activeVotes.map((vote) => {
          const needed = Math.max(1, vote.maxParticipants - 1);
          const isReady = vote.slots.some(
            (s) => s.votesCount >= needed && s.isAvailable !== false
          );
          const mostVoted = vote.slots.reduce(
            (best, s) => (s.votesCount > (best?.votesCount ?? 0) ? s : best),
            vote.slots[0] as (typeof vote.slots)[0] | undefined
          );
          return (
            <DropdownMenuItem
              key={vote.id}
              onSelect={(e) => {
                if ((e.target as HTMLElement).closest('[data-delete-vote]')) {
                  e.preventDefault();
                  return;
                }
                handleVoteSelect(vote);
              }}
              className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
            >
              <div className="flex w-full items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <span className="font-medium truncate block">
                    {vote.activityName}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    {isReady ? (
                      <>
                        <CheckCircle2 className="size-3 shrink-0 text-green-600" />
                        Prêt à réserver
                      </>
                    ) : mostVoted ? (
                      <>
                        {mostVoted.votesCount} vote
                        {mostVoted.votesCount > 1 ? 's' : ''} ·{' '}
                        {format(new Date(mostVoted.slotDate), 'd MMM', {
                          locale: fr,
                        })}{' '}
                        à {formatTimeDisplay(mostVoted.slotTime)}
                      </>
                    ) : (
                      'En attente de votes'
                    )}
                  </span>
                </div>
                <Button
                  data-delete-vote
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10 -mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(false);
                    setVoteToDelete(vote);
                  }}
                  aria-label="Terminer ce vote"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
      <AlertDialog
        open={!!voteToDelete}
        onOpenChange={(open) => !open && setVoteToDelete(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce vote ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les participants ne pourront plus voter. Cette action est
              irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DropdownMenu>
  );
};

export default GroupVoteStickyIndicator;
